import React from 'react';
import { motion } from 'framer-motion';
// Updated icons for cruise types/features
import { IoSunnyOutline, IoMoonOutline } from "react-icons/io5";
import { FaAnchor, FaWater } from "react-icons/fa"; // Using FaAnchor or FaWater
import { MdOutlineExplore } from "react-icons/md"; // Example for exploration/destinations
import { FaPhoneAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Renamed component
const CruiseFeatureColumn = ({ icon: Icon, title, description }) => (
  <motion.div
    className="flex flex-col items-center text-center p-8 md:p-12 lg:p-16 border-b md:border-b-0 md:border-r last:border-r-0 border-gray-200 bg-white rounded-b-md md:rounded-none md:first:rounded-l-md md:last:rounded-r-md shadow-lg"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
  >
    <div className="mb-6 text-5xl text-blue-500">
      <Icon />
    </div>
    <h3 className="text-xl font-semibold mb-4 JosefinSans text-gray-800">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

// Renamed component
const FeaturedCruises = () => {
  const navigate = useNavigate();

  // Updated features for yacht cruises
  const cruiseFeatures = [
    {
      icon: IoSunnyOutline,
      title: "Half-Day Cruises (Morning/Sunset)",
      description: "Choose between a refreshing morning sail or a magical sunset experience along the stunning Mykonos coastline or towards Delos & Rhenia islands. Perfect for a shorter escape."
    },
    {
      icon: FaWater, // Or FaAnchor
      title: "Full-Day Private Cruises",
      description: "Immerse yourself in a full day of luxury. Combine destinations like the South Coast and Delos/Rhenia, enjoy extended swimming stops, and savor the all-inclusive amenities at your leisure."
    },
    {
      icon: MdOutlineExplore,
      title: "Iconic Destinations",
      description: "Explore the famous South Coast beaches (Paradise, Super Paradise, etc.) or journey to the historic islands of Delos and Rhenia. Tailor your private cruise to your desired spots."
    }
  ];

  const handleExploreClick = () => {
    // Navigate to the main yacht listing page or a dedicated cruises page
    navigate('/cruises');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Updated section ID */}
      <section id='cruises' className="py-16 relative mx-auto overflow-hidden bg-gray-50">
        <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row gap-12 px-4 md:px-8 items-center">

          {/* Left Section (CTA) - Swapped order for better flow on large screens */}
          <motion.div
            className="lg:w-1/3 w-full flex flex-col justify-center text-center lg:text-left mb-8 lg:mb-0"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl lg:text-6xl JosefinSans text-blue-900 font-semibold">Discover Our Cruises</h2>
              <p className="text-gray-600 text-md">
                Embark on an unforgettable journey with our curated Mykonos yacht cruises. All-inclusive luxury awaits.
              </p>
              <hr className="w-24 border-2 border-red-500 mx-auto lg:mx-0" />
            </div>

            <div className="space-y-4 mt-8 flex flex-col items-center lg:items-start">
              <motion.button
                onClick={handleExploreClick}
                className="w-full sm:w-2/3 lg:w-3/4 flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-4 rounded-lg hover:bg-blue-900 transition-all duration-300 shadow-md"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaAnchor className="text-xl" />
                <span>EXPLORE CRUISES</span>
              </motion.button>

              <div className="text-gray-600 font-semibold">OR</div>

              <motion.button
                onClick={() => window.location.href = 'tel:+306941419012'}
                className="w-full sm:w-2/3 lg:w-3/4 flex items-center justify-center gap-2 bg-gray-700 text-white px-6 py-4 rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-md"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaPhoneAlt />
                <span>CALL US NOW</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Right Section (Features) - Swapped order */}
          <motion.div
            className="lg:w-2/3 w-full"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 border border-gray-200 rounded-md overflow-hidden shadow-xl">
              {cruiseFeatures.map((feature, index) => (
                <CruiseFeatureColumn key={index} {...feature} />
              ))}
            </div>
          </motion.div>

        </div>
      </section>

      {/* Removed TransferModal */}
    </>
  );
};

export default FeaturedCruises; // Export renamed component
